import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { StarIcon, ArrowRightIcon, MapPinIcon } from 'lucide-react';
// Shared mock data for reviews
export const allReviews = [{
  id: 1,
  author: 'Sarah Jenkins',
  platform: 'Google',
  location: 'Mitchellville',
  rating: 5,
  date: 'March 10, 2026',
  content: 'State-of-the-art equipment and a beautiful, clean office. I brought my 5-year-old in for her first exam and they were fantastic with her. Highly recommend Dr. Nnabue!'
}, {
  id: 2,
  author: 'Michael Rodriguez',
  platform: 'Yelp',
  location: 'Silver Spring',
  rating: 5,
  date: 'February 28, 2026',
  content: 'I was nervous about getting contacts for the first time, but the team here was incredibly patient. They walked me through the whole process. Great experience from start to finish.'
}, {
  id: 3,
  author: 'Emily Thompson',
  platform: 'Facebook',
  location: 'Frederick',
  rating: 5,
  date: 'February 15, 2026',
  content: "The staff at VisualEyes made my eye exam a breeze. Dr. Chen took the time to explain everything clearly. I've never felt so cared for at a doctor's office!"
}, {
  id: 4,
  author: 'David Chen',
  platform: 'Google',
  location: 'Greenbelt',
  rating: 5,
  date: 'January 22, 2026',
  content: 'After struggling with dry eyes for years, they finally found a treatment plan that works. My quality of life has improved dramatically. The front desk staff is also very welcoming.'
}, {
  id: 5,
  author: 'Jessica Williams',
  platform: 'Google',
  location: 'Clinton',
  rating: 5,
  date: 'January 10, 2026',
  content: 'Fast, efficient, and very professional. I was in and out for my annual exam in under 45 minutes, but never felt rushed. Great selection of frames too!'
}, {
  id: 6,
  author: 'Robert Taylor',
  platform: 'Yelp',
  location: 'Hyattsville',
  rating: 4,
  date: 'December 05, 2025',
  content: 'Very thorough exam. The doctor explained my astigmatism in a way I finally understood. Deducted one star because they were running about 15 minutes behind, but the care was excellent.'
}, {
  id: 7,
  author: 'Amanda Martinez',
  platform: 'Facebook',
  location: 'Mitchellville',
  rating: 5,
  date: 'November 18, 2025',
  content: "I've been coming here for 5 years and the service is consistently excellent. They helped me pick out the perfect frames that fit my face shape perfectly."
}, {
  id: 8,
  author: 'James Wilson',
  platform: 'Google',
  location: 'Silver Spring',
  rating: 5,
  date: 'November 02, 2025',
  content: "Best optometrist I've ever visited. The technology they use is amazing - I didn't even have to get my eyes dilated because of their advanced imaging machines."
}];
// Helper to get platform colors
export const getPlatformColor = (_platform: string) => {
  return 'bg-brand-gold/10 text-brand-gold';
};
export function ReviewExcerpts() {
  // Take only the first 3 for the homepage
  const recentReviews = allReviews.slice(0, 3);
  return <section className="py-20 md:py-28 bg-surface-muted" data-id="element-443">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-444">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-445">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-section-mobile md:text-section text-brand-dark mb-4 font-heading font-bold" data-id="element-446">
            Real Patient Stories
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-subtitle text-neutral-text/80 font-body" data-id="element-447">
            See what our community is saying about their experience at our
            clinics across Maryland.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-448">
          {recentReviews.map((review, index) => <motion.div key={review.id} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="bg-white p-8 rounded-card shadow-card flex flex-col h-full relative" data-id="element-449">
              {/* Platform Badge */}
              <div className="absolute top-6 right-6" data-id="element-450">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm ${getPlatformColor(review.platform)}`} data-id="element-451">
                  {review.platform}
                </span>
              </div>

              {/* Rating */}
              <div className="flex gap-1 mb-4" data-id="element-452">
                {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-brand-gold text-brand-gold' : 'text-gray-200'}`} data-id="element-453" />)}
              </div>

              {/* Content */}
              <p className="text-neutral-text/80 italic mb-6 text-base leading-relaxed flex-grow" data-id="element-454">
                "{review.content}"
              </p>

              {/* Author & Location */}
              <div className="mt-auto pt-4 border-t border-gray-100" data-id="element-455">
                <div className="flex items-center justify-between" data-id="element-456">
                  <span className="font-heading font-semibold text-brand-dark text-base" data-id="element-457">
                    {review.author}
                  </span>
                  <div className="flex items-center gap-1 text-sm text-neutral-text/60" data-id="element-458">
                    <MapPinIcon className="w-3 h-3 text-brand-gold" data-id="element-459" />
                    {review.location}
                  </div>
                </div>
              </div>
            </motion.div>)}
        </div>

        <div className="text-center" data-id="element-460">
          <Link to="/reviews" className="inline-flex items-center gap-2 bg-white border-2 border-brand-dark text-brand-dark px-8 py-3.5 rounded-button font-heading font-semibold hover:bg-brand-dark hover:text-white transition-colors shadow-sm focus-ring group" data-id="element-461">
            View All Reviews
            <ArrowRightIcon className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" data-id="element-462" />
          </Link>
        </div>
      </div>
    </section>;
}